import type { FC } from 'react';

import ListingDetailPanel from '@/common-components/ListingDetailPanel';
import SideDrawer from '@/common-components/SideDrawer';
import type { UseMediaGalleryResult } from '@/hooks/useMediaGallery.hook';
import type { AdminListingDetail, AdminListingListItem } from '@/types/rental.types';

interface ListingDetailDrawerProps {
  selectedListing: AdminListingListItem | null;
  detail: AdminListingDetail | undefined;
  gallery: UseMediaGalleryResult;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  mutatingId: string | null;
  onClose: () => void;
  onRemove: (listing: AdminListingListItem) => void;
  onRestore: (listing: AdminListingListItem) => void;
}

const footerBtn =
  'w-full rounded-lg border py-2.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const ListingDetailDrawer: FC<ListingDetailDrawerProps> = ({
  selectedListing,
  detail,
  gallery,
  isLoadingDetail,
  isErrorDetail,
  mutatingId,
  onClose,
  onRemove,
  onRestore,
}) => {
  const isMutating = !!(selectedListing && mutatingId === selectedListing.id);
  const status = detail?.status ?? selectedListing?.status;

  const footer =
    selectedListing && status ? (
      status === 'removed' ? (
        <button
          type="button"
          disabled={isMutating}
          onClick={() => onRestore(selectedListing)}
          className={`${footerBtn} border-[var(--color-success)] bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white`}
        >
          {isMutating ? 'Restoring…' : 'Restore Listing'}
        </button>
      ) : (
        <button
          type="button"
          disabled={isMutating}
          onClick={() => onRemove(selectedListing)}
          className={`${footerBtn} border-[var(--color-danger)] bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white`}
        >
          {isMutating ? 'Removing…' : 'Remove Listing'}
        </button>
      )
    ) : undefined;

  return (
    <SideDrawer
      isOpen={selectedListing !== null}
      title="Listing Details"
      onClose={onClose}
      footer={footer}
    >
      <ListingDetailPanel
        title={selectedListing?.title ?? ''}
        category={selectedListing?.category}
        status={selectedListing?.status}
        campusName={selectedListing?.campusName}
        detail={detail}
        gallery={gallery}
        isLoading={isLoadingDetail}
        isError={isErrorDetail}
      />
    </SideDrawer>
  );
};

export default ListingDetailDrawer;
